import { useEffect, useState } from "react";

import type { SortingState } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  getFilteredRowModel,
} from "@tanstack/react-table";

import type { VirtualizedTableProps } from "@/shared/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";

import { cn } from "@/shared/lib/utils";
import { Skeleton } from "../skeleton";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { VirtualizedMobileDataTable } from "./VirtualizedMobileDataTable";
import { useTieAccountsStore } from "@/entities/user/store";
import { ActiveFilters } from "@/features/ActiveFilters";
// import { MobileDataTable } from "./MobileDataTable";

export function VirtualizedDataTable<TData, TValue>({
  columns,
  data,
  isLoading,
  selectedState,
  setSelectedState,
  setAllSelectedState,
  getRowId,
  selectMobileColumn,
  refetch,
}: VirtualizedTableProps<TData, TValue>) {
  const { allSelected } = useTieAccountsStore();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState({});

  const isMobile = useIsMobile();

  useEffect(() => {
    if (setSelectedState) {
      const rows = table.getSelectedRowModel().rows;
      setSelectedState(rows.map((row) => row.original));
    }
  }, [rowSelection]);

  useEffect(() => {
    const rows = table.getSelectedRowModel().rows;
    if (rows.length > 0 && selectedState?.length === 0) {
      setRowSelection({});
    }

    table.getIsAllPageRowsSelected() && setAllSelectedState?.(true);
  }, [selectedState]);

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    enableRowSelection: true,
    getSortedRowModel: getSortedRowModel(),
    getRowId,
    state: { sorting, rowSelection },
    onRowSelectionChange: setRowSelection,
    getFilteredRowModel: getFilteredRowModel(),
    debugTable: true,
  });

  if (isMobile) {
    return (
      <VirtualizedMobileDataTable
        data={data}
        isLoading={isLoading}
        table={table}
        selectMobileColumn={selectMobileColumn}
        isVirtualized={true}
        refetch={refetch}
      />
    );
  }

  return (
    <div className="relative mt-2">
      {refetch && <ActiveFilters refetch={refetch} />}
      <div className="rounded-md border border-strokeColor">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className={cn("px-4")}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(10)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell className="pl-4 pr-0">
                    <Skeleton className="h-9 w-full rounded-r-none" />
                  </TableCell>
                  {table.getHeaderGroups()[0].headers.map((_, cellIndex) => (
                    <TableCell className="px-0" key={cellIndex}>
                      <Skeleton className="h-9 w-full rounded-l-none rounded-r-none" />
                    </TableCell>
                  ))}
                  <TableCell className="pl-0 pr-4">
                    <Skeleton className="h-9 w-full rounded-l-none" />
                  </TableCell>
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={
                    (row.getIsSelected() || allSelected) && "selected"
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell className="px-4 text-textColor" key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-textColor"
                >
                  Ничего нет.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
